import { Fragment, useState } from 'react';
import { shortUrl } from '@/lib/format';
import type { ValueSource } from '@shared/types';
import { Icon } from './Icon';
import { usePopoverClose } from './Popover';

// Where the AI found a value: the pages and registers it names, as links that open in a new tab.

/** The words a source shows as: the AI's name for it, else its short address. */
export function sourceLabel(s: ValueSource): string {
  return s.name ?? (s.url ? shortUrl(s.url) : 'Web search');
}

/** The hover text: the page's own title, else the full address when the label is a name. */
function sourceTip(s: ValueSource): string | undefined {
  return s.title ?? (s.name && s.url ? s.url : undefined);
}

function isLinkedIn(url: string | null): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host === 'linkedin.com' || host.endsWith('.linkedin.com');
  } catch {
    return false;
  }
}

/** One copy of each source, in order: the same page named twice shows once. */
export function uniqueSources(list: ValueSource[]): ValueSource[] {
  const seen = new Set<string>();
  return list.filter((s) => {
    const key = s.url ?? `name:${s.name ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** "a, b and c", or the first few and "and 2 more", which shows the rest. */
export function SourceLinks({ sources, max = 3, className }: { sources: ValueSource[]; max?: number; className?: string }) {
  const [all, setAll] = useState(false);
  const list = uniqueSources(sources);
  // Hiding a single source would take as much room as showing it.
  const shown = all || list.length <= max + 1 ? list : list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <span className={['srcs', className].filter(Boolean).join(' ')}>
      {shown.map((s, i) => (
        <Fragment key={`${s.url ?? s.name}-${i}`}>
          {i === 0 ? null : i === shown.length - 1 && !rest ? ' and ' : ', '}
          {s.url ? (
            <a className="src-link" href={s.url} target="_blank" rel="noreferrer" title={sourceTip(s)}>
              {sourceLabel(s)}
            </a>
          ) : (
            <span className="src-name">{sourceLabel(s)}</span>
          )}
        </Fragment>
      ))}
      {rest ? (
        <>
          {' and '}
          <button type="button" className="src-more" onClick={() => setAll(true)} aria-label={`Show ${rest} more sources`}>
            {rest} more
          </button>
        </>
      ) : null}
    </span>
  );
}

/** Sources as menu rows in a popover: the name or page title over its address, each opening the page. */
export function SourceMenuItems({ sources }: { sources: ValueSource[] }) {
  const close = usePopoverClose();
  return (
    <>
      {uniqueSources(sources).map((s, i) => {
        const main = s.name ?? s.title ?? (s.url ? shortUrl(s.url) : 'Web search');
        const sub = s.url && shortUrl(s.url) !== main ? shortUrl(s.url) : null;
        const body = (
          <>
            <Icon name={isLinkedIn(s.url) ? 'linkedin' : 'globe'} />
            <span className="grow mi-two">
              <span>{main}</span>
              {sub ? <small>{sub}</small> : null}
            </span>
            {s.url ? <Icon name="external" className="src-out" /> : null}
          </>
        );
        return s.url ? (
          <a key={`${s.url}-${i}`} className="mi src-mi" role="menuitem" href={s.url} target="_blank" rel="noreferrer" title={s.title ?? s.url} onClick={close}>
            {body}
          </a>
        ) : (
          <div key={`${s.name}-${i}`} className="src-row">
            {body}
          </div>
        );
      })}
    </>
  );
}
